import { DefaultValues } from 'react-hook-form'

import { format, parseISO } from 'date-fns'
import { flow } from 'lodash'
import { isNil } from 'lodash-es'

import {
  PickupDeliveryLocationListResponseV1,
  PickupDeliveryLocationResponseV1,
} from '@forto-dev/customer-master-data-models'
import { OriginLocalHandlingProductCode } from '@forto-dev/shipment-models'
import { Point, Sheet } from '@freighthub/freighthub-quotation-sdk'

import {
  AddressDetailsDTO,
  BookingDTOCargoFCL,
  BookingDTOConsignee,
  BookingDTOControllingParty,
  BookingDTOImportCustomsServiceClearanceTypes,
  BookingDTOTransportMode,
  BookingDTOWaypointsInner as BookingDTOWaypoints,
  ContainerItemDTO,
  CreateBookingDTO,
  CreateBookingDTOCargoLCL,
  type CreateBookingDTODestinationLocalHandlingService,
  type CreateBookingDTOOriginLocalHandlingService,
  CreateBookingDTOTransportType,
  type DestinationLocalHandlingProductDTOCode,
  GetPricesDTO,
  GetPricesDTOTransportModes,
  InsuranceDTO,
  LCLItemDTO,
  type OriginLocalHandlingProductDTOCode,
  PackageDTO,
  WaypointBaseDTOTransportMode,
} from '@/clients/booking'
import { CargoType } from '@/features/bookings/api/cargoTypes'
import { incotermCodeToNamedPlaceOfDelivery, mapIncotermToUniqueKey } from '@/features/bookings/api/incoterms'
import { getPortById } from '@/features/bookings/api/ports'
import { RequestManualQuoteDTO } from '@/features/bookings/api/quotes'
import { LocalHandlingHelpers } from '@/features/bookings/api/sanitizeOriginLocalHandlingService'
import { Mapped } from '@/features/bookings/types/Mapped'
import { createAddressDetailsFromLocation, formatAddressDetails } from '@/features/bookings/utils/address'
import {
  defaultWaypoints,
  setDeliveryAddress,
  setDestinationPort,
  setMainCarriageHandledByThirdParty,
  setMainCarriageTransportMode,
  setOnCarriageTransportMode,
  setOriginPort,
  setPickupAddress,
  setPreCarriageTransportMode,
} from '@/features/bookings/utils/waypoints'
import { isAddress, isPort } from '@/features/trackTrace'
import { error as logError } from '@/utils/logger'

import { isSeaFcl } from './serviceUtils'
import { flattenCargoFCLForSubmit } from '../components/BookingDetailsStep/FCLCargoDetails/containerGrossWeightTransforms'
import { PricingResponseShipmentWithManualQuote } from '../components/BookingFinalStep/parsePricingResponse'
import { BookingCustomType } from '../hooks/useBookingCustomTypeLabels'
import { TranslatedCommodity } from '../hooks/useTranslatedCommodities'
import { BookingWizardFormState, LclCargoType } from '../types/BookingWizardFormState'

export const bookingDefaultFclContainer: DefaultValues<ContainerItemDTO> = {}

export const bookingDefaultCargoPackage: DefaultValues<PackageDTO> = {}

export const defaultFCLCargo: DefaultValues<BookingDTOCargoFCL> = {
  packages: [bookingDefaultCargoPackage],
  containers: [bookingDefaultFclContainer],
}

export const defaultLCLCargoByDimensionsItem: DefaultValues<LCLItemDTO> = {
  quantity: 0,
  weight: 0,
  volume: 0,
  dimensions: {
    lengthCm: 0,
    widthCm: 0,
    heightCm: 0,
  },
}

export const defaultLCLCargoByWeightAndVolItem: DefaultValues<LCLItemDTO> = {
  quantity: 0,
  weight: 0,
  volume: 0,
}

export const defaultLCLCargo: DefaultValues<CreateBookingDTOCargoLCL> = {
  isStackableGoods: true,
}

export const isCDIncoterm = (code?: string) => code?.charAt(0) === 'C' || code?.charAt(0) === 'D'

export const bookingWizardDefaultBooking: DefaultValues<BookingWizardFormState> = {
  controllingParty: BookingDTOControllingParty.Consignee,
  cargoFCL: defaultFCLCargo,
  cargoLCL: defaultLCLCargo,
  lclCargoType: LclCargoType.ByDimensions,
  cargoLCLByWeightAndVolItems: [defaultLCLCargoByWeightAndVolItem],
  cargoLCLByDimensionsItems: [defaultLCLCargoByDimensionsItem],

  // Wizard-only fields (some are not mapped to backend)
  extraLocalHandlingServicesEnabled: false,
  mainCarriageDocumentationType: undefined,
  originLocalHandlingService: [],
  destinationLocalHandlingService: [],
  isMainCarriageHandledByThirdParty: false,
}

// The booking-v2-api code union still lags the models package, so widen once here
export const VGMO_CODE = OriginLocalHandlingProductCode.VGMO as OriginLocalHandlingProductDTOCode
export const VGMSO_CODE = OriginLocalHandlingProductCode.VGMSO as OriginLocalHandlingProductDTOCode

const withoutProductCode = (
  originLocalHandlingService: CreateBookingDTOOriginLocalHandlingService | undefined,
  code: OriginLocalHandlingProductDTOCode
): CreateBookingDTOOriginLocalHandlingService | undefined => {
  const products = (originLocalHandlingService?.products ?? []).filter((product) => product.code !== code)

  return products.length ? { products } : undefined
}

const mapOriginLocalHandlingProductCodes = (
  codes: string[] | undefined
): CreateBookingDTOOriginLocalHandlingService | undefined => {
  const selectedCodes = codes ?? []

  const uniqueCodesInOrder: string[] = []
  const seen = new Set<string>()
  for (const code of selectedCodes) {
    if (!code || seen.has(code)) continue
    seen.add(code)
    uniqueCodesInOrder.push(code)
  }

  if (uniqueCodesInOrder.length === 0) return undefined

  return {
    products: uniqueCodesInOrder.map((code) => ({
      code: code as unknown as OriginLocalHandlingProductDTOCode,
      name: code,
    })),
  }
}

const mapDestinationLocalHandlingProductCodes = (
  codes: string[] | undefined
): CreateBookingDTODestinationLocalHandlingService | undefined => {
  const selectedCodes = codes ?? []

  const uniqueCodesInOrder: string[] = []
  const seen = new Set<string>()
  for (const code of selectedCodes) {
    if (!code || seen.has(code)) continue
    seen.add(code)
    uniqueCodesInOrder.push(code)
  }

  if (uniqueCodesInOrder.length === 0) return undefined

  return {
    products: uniqueCodesInOrder.map((code) => ({
      code: code as unknown as DestinationLocalHandlingProductDTOCode,
      name: code,
    })),
  }
}

const mapMainCarriageDocumentTypeToProducts = (type: string | undefined) => {
  if (!type) return undefined

  // mainCarriageDocumentationType is a local handling product code selected from the dropdown
  // (e.g. TLXO, SWHBLO, _courier_service_temp, VCO, HBSLTO)
  return {
    products: [{ code: type, name: type }],
  }
}

type MapBookingWizardFormStateToCreateBookingDTOArgs = {
  data: BookingWizardFormState
  customerID: string
  isMultipleCommoditiesUnNumbersEnabled: boolean
  isNewCustomsServiceEnabled: boolean
  isLocalHandlingServicesEnabled?: boolean
  isVgmLocalHandlingServicesEnabled?: boolean
}
export const mapBookingWizardFormStateToCreateBookingDTO = async ({
  data,
  customerID,
  isMultipleCommoditiesUnNumbersEnabled,
  isNewCustomsServiceEnabled,
  isLocalHandlingServicesEnabled = false,
  isVgmLocalHandlingServicesEnabled = false,
}: MapBookingWizardFormStateToCreateBookingDTOArgs): Promise<
  Mapped<CreateBookingDTO> & Partial<LocalHandlingHelpers>
> => {
  const waypoints: BookingDTOWaypoints[] = buildWaypointsFromWizardFormState(data)
  const sanitizedCargo = sanitizeBookingCargo(data, isMultipleCommoditiesUnNumbersEnabled)
  const sanitizedCustoms = await sanitizeBookingCustoms({
    customs: data.customs,
    importCustomsService: data.importCustomsService,
    exportCustomsService: data.exportCustomsService,
    isNewCustomsServiceEnabled,
    originPortId: data.originPort,
    destinationPortId: data.destinationPort,
  })

  const selectedOriginLocalHandlingService =
    isLocalHandlingServicesEnabled && data.extraLocalHandlingServicesEnabled
      ? mapOriginLocalHandlingProductCodes(data.originLocalHandlingService)
      : undefined

  // Both VGM services are Sea FCL only, so a lookup and a VGMO selection are pointless otherwise
  const isVgmEligibleRoute = isVgmLocalHandlingServicesEnabled && isSeaFcl(data.transportType, data.mainCarriageMode)
  // Both VGM services are Sea FCL only, so drop selections left over from an earlier route
  const originProductsForRoute = isVgmEligibleRoute
    ? selectedOriginLocalHandlingService
    : withoutProductCode(withoutProductCode(selectedOriginLocalHandlingService, VGMO_CODE), VGMSO_CODE)

  const originLocalHandlingService = originProductsForRoute
  const destinationLocalHandlingService =
    isLocalHandlingServicesEnabled && data.extraLocalHandlingServicesEnabled
      ? mapDestinationLocalHandlingProductCodes(data.destinationLocalHandlingService)
      : undefined

  const mainCarriageDocumentType =
    isLocalHandlingServicesEnabled && data.extraLocalHandlingServicesEnabled
      ? mapMainCarriageDocumentTypeToProducts(data.mainCarriageDocumentationType)
      : undefined

  return {
    cargoReadyAt: data.cargoReadyAt,
    requestedPickupAt: data.requestedPickupAt,
    requestedDeliveryAt: data.requestedDeliveryAt,
    requestedEtd: data.requestedEtd,
    requestedEta: data.requestedEta,
    clonedFrom: data.clonedFrom,
    waypoints,
    customerID,
    initiatorParty: data.controllingParty,
    controllingParty: data.controllingParty,
    tags: data.tags,
    transportType: data.transportType,
    incoterm: {
      ...data.incoterm,
      paymentTerms: isCDIncoterm(data.incoterm?.code) ? 'prepaid' : data.incoterm?.paymentTerms,
    },
    insurance: data.insurance,
    customs: sanitizedCustoms.customs,
    importCustomsService: sanitizedCustoms.importCustomsService,
    exportCustomsService: sanitizedCustoms.exportCustomsService,
    originLocalHandlingService,
    destinationLocalHandlingService,
    mainCarriageDocumentType,
    carbonOffsetContribution: data.carbonOffsetContribution,
    hasDangerousGoods: data.hasDangerousGoods,
    dangerousGoods: undefined,
    shipperContactID: data.shipperContactID || undefined,
    consigneeContactID: data.consigneeContactID || undefined,
    shipperLegalEntityID: data.shipperLegalEntityID || undefined,
    consigneeLegalEntityID: data.consigneeLegalEntityID || undefined,
    // Legacy VGM flag, only sent while the VGM local handling flag is off (TTP-7414)
    isVGMBooking: isVgmLocalHandlingServicesEnabled ? undefined : data.isVGMBooking,
    isBCNBooking: data.isBCNBooking,
    cargoFCL: sanitizedCargo.cargoFCL,
    cargoLCL: sanitizedCargo.cargoLCL,
    unNumbers: data.hasDangerousGoods ? data.unNumbers : undefined,
    internalNotes: '',
    consignee: data.consignee as BookingDTOConsignee,
    shipper: data.shipper as BookingDTOConsignee,
    shipperLegalEntityCompanyName: data.shipperLegalEntityCompanyName,
    consigneeLegalEntityCompanyName: data.consigneeLegalEntityCompanyName,
    sustainabilityService: data.sustainabilityService,

    onBehalfOfConsigneeLegalEntityID: data.onBehalfOfConsigneeLegalEntityID || undefined,
    onBehalfOfShipperLegalEntityID: data.onBehalfOfShipperLegalEntityID || undefined,
    onBehalfOfShipperContactID: data.onBehalfOfShipperContactID || undefined,
    onBehalfOfConsigneeContactID: data.onBehalfOfConsigneeContactID || undefined,

    bookingConfirmationRecipients: data.bookingConfirmationRecipients?.length
      ? data.bookingConfirmationRecipients
      : undefined,

    shortenedBooking: false,

    originAssignedPartnerAgent: undefined,
    destinationAssignedPartnerAgent: undefined,
    originHandlingBillingEntityID: undefined,
    destinationHandlingBillingEntityID: undefined,
    hblNotifyPartyContactID: undefined,
    hblNotifyPartyLegalEntityID: undefined,
    mblNotifyPartyContactID: undefined,
    mblNotifyPartyLegalEntityID: undefined,
  }
}

export const buildWaypointsFromWizardFormState = (data: BookingWizardFormState): BookingDTOWaypoints[] => {
  const {
    originPort,
    destinationPort,
    pickupAddressDetails,
    deliveryAddressDetails,
    mainCarriageMode,
    onCarriageMode,
    preCarriageMode,
    isMainCarriageHandledByThirdParty,
  } = data

  return flow(
    (wps) => (mainCarriageMode?.[0] != null ? setMainCarriageTransportMode(wps, mainCarriageMode[0]) : wps),
    (wps) => (originPort ? setOriginPort(wps, originPort) : wps),
    (wps) => (destinationPort ? setDestinationPort(wps, destinationPort) : wps),
    (wps) => (pickupAddressDetails ? setPickupAddress(wps, pickupAddressDetails) : wps),
    (wps) => (deliveryAddressDetails ? setDeliveryAddress(wps, deliveryAddressDetails) : wps),
    (wps) => (onCarriageMode ? setOnCarriageTransportMode(wps, onCarriageMode) : wps),
    (wps) => (preCarriageMode ? setPreCarriageTransportMode(wps, preCarriageMode) : wps),
    (wps) => (isMainCarriageHandledByThirdParty ? setMainCarriageHandledByThirdParty(wps, true) : wps)
  )(defaultWaypoints as BookingDTOWaypoints[])
}

type MapBookingWizardFormStateToGetPricesDTOMappedArgs = {
  data: BookingWizardFormState
  customerID: string
  isMultipleCommoditiesUnNumbersEnabled: boolean
  isNewCustomsServiceEnabled: boolean
  isLocalHandlingServicesEnabled?: boolean
  isVgmLocalHandlingServicesEnabled?: boolean
}

type WizardGetPricesDTO = Mapped<Omit<GetPricesDTO, 'serviceType'>> &
  Partial<Pick<CreateBookingDTO, 'originLocalHandlingService' | 'destinationLocalHandlingService'>>

export const mapBookingWizardFormStateToGetPricesDTO = async ({
  data,
  customerID,
  isMultipleCommoditiesUnNumbersEnabled,
  isNewCustomsServiceEnabled,
  isLocalHandlingServicesEnabled = false,
  isVgmLocalHandlingServicesEnabled = false,
}: MapBookingWizardFormStateToGetPricesDTOMappedArgs): Promise<WizardGetPricesDTO> => {
  const createBookingDTO = await mapBookingWizardFormStateToCreateBookingDTO({
    data,
    customerID,
    isMultipleCommoditiesUnNumbersEnabled,
    isNewCustomsServiceEnabled,
    isLocalHandlingServicesEnabled,
    isVgmLocalHandlingServicesEnabled,
  })

  let transportModes = undefined
  if ((data.mainCarriageMode?.length ?? 0) > 1) {
    transportModes = data.mainCarriageMode as unknown as GetPricesDTOTransportModes
  }

  return {
    transportType: createBookingDTO.transportType,
    transportModes,
    waypoints: createBookingDTO.waypoints || [],
    cargoFCL: createBookingDTO.cargoFCL!,
    cargoLCL: createBookingDTO.cargoLCL!,
    consigneeLegalEntityID: createBookingDTO.consigneeLegalEntityID,
    shipperLegalEntityID: createBookingDTO.shipperLegalEntityID,
    incoterm: {
      ...createBookingDTO.incoterm,
      paymentTerms: isCDIncoterm(data.incoterm?.code) ? 'prepaid' : data.incoterm?.paymentTerms,
    },
    cargoReadyAt: createBookingDTO.cargoReadyAt,
    insurance: createBookingDTO.insurance,
    customs: createBookingDTO.customs,
    carbonOffsetContribution: createBookingDTO.carbonOffsetContribution,
    hasDangerousGoods: createBookingDTO.hasDangerousGoods,
    isVGMBooking: createBookingDTO.isVGMBooking,
    isBCNBooking: createBookingDTO.isBCNBooking,
    specialEquipment: data.specialEquipment,
    unNumbers: createBookingDTO.unNumbers,
    controllingParty: createBookingDTO.controllingParty,
    importCustomsService: createBookingDTO.importCustomsService,
    exportCustomsService: createBookingDTO.exportCustomsService,
    sustainabilityService: createBookingDTO.sustainabilityService,
    originLocalHandlingService: createBookingDTO.originLocalHandlingService,
    destinationLocalHandlingService: createBookingDTO.destinationLocalHandlingService,
  }
}

type SanitizeBookingCustomsArgs = Pick<
  BookingWizardFormState,
  'customs' | 'importCustomsService' | 'exportCustomsService'
> & {
  isNewCustomsServiceEnabled: boolean
  originPortId?: string
  destinationPortId?: string
}
const sanitizeBookingCustoms = async ({
  customs,
  importCustomsService,
  exportCustomsService,
  isNewCustomsServiceEnabled,
  originPortId,
  destinationPortId,
}: SanitizeBookingCustomsArgs): Promise<
  Pick<CreateBookingDTO, 'customs' | 'importCustomsService' | 'exportCustomsService'>
> => {
  if (isNewCustomsServiceEnabled && (importCustomsService != null || exportCustomsService != null)) {
    let originPort
    let destinationPort
    if (originPortId) {
      originPort = await getPortById(originPortId)
    }
    if (destinationPortId) {
      destinationPort = await getPortById(destinationPortId)
    }

    return {
      importCustomsService:
        importCustomsService &&
        importCustomsService.clearanceTypes != null &&
        importCustomsService.clearanceTypes.length > 0
          ? {
              ...importCustomsService,
              customsLocations: destinationPort
                ? [
                    {
                      port: {
                        id: destinationPort.id,
                        name: destinationPort.name,
                        countryCode: destinationPort.countryCode,
                      },
                    },
                  ]
                : undefined,
            }
          : undefined,
      exportCustomsService:
        exportCustomsService &&
        exportCustomsService.clearanceTypes != null &&
        exportCustomsService.clearanceTypes.length > 0
          ? {
              ...exportCustomsService,
              customsLocations: originPort
                ? [
                    {
                      port: {
                        id: originPort.id,
                        name: originPort.name,
                        countryCode: originPort.countryCode,
                      },
                    },
                  ]
                : undefined,
            }
          : undefined,
    }
  }

  return {
    customs: customs
      ? {
          ...customs,
          originCustomsType: customs.originCustomsType === 'none' ? undefined : customs.originCustomsType,
          destinationCustomsType:
            customs.destinationCustomsType === 'none' ? undefined : customs.destinationCustomsType,
        }
      : customs,
  }
}

export const sanitizeBookingCargo = (
  data: BookingWizardFormState,
  isMultipleCommoditiesUnNumbersEnabled: boolean
): Pick<CreateBookingDTO, 'cargoFCL' | 'cargoLCL'> => {
  const { transportType, cargoFCL } = data

  if (transportType === CreateBookingDTOTransportType.Fcl && cargoFCL) {
    const flattenedCargoFCL = flattenCargoFCLForSubmit(cargoFCL)
    const { commodities, commodity, commodityID, ...cargoFCLRest } = flattenedCargoFCL
    const commodityData = processCommodityData({
      commodities,
      commodity,
      commodityID,
      isMultipleCommoditiesUnNumbersEnabled,
    })
    return {
      cargoFCL: {
        ...cargoFCLRest,
        ...commodityData,
        packages: flattenedCargoFCL?.packages?.filter((p) => p.quantity != null && p.packageTypeCode != null),
      } as BookingDTOCargoFCL,
      cargoLCL: undefined,
    }
  }

  if (transportType === CreateBookingDTOTransportType.Lcl) {
    return {
      cargoLCL: getBookingDTOCargoLCLFromWizardFormState(data, isMultipleCommoditiesUnNumbersEnabled),
      cargoFCL: undefined,
    }
  }

  return {}
}

export const getBookingDTOCargoLCLFromWizardFormState = (
  data: BookingWizardFormState,
  isMultipleCommoditiesUnNumbersEnabled: boolean
): CreateBookingDTOCargoLCL => {
  const { cargoLCL = {}, cargoLCLByDimensionsItems, cargoLCLByWeightAndVolItems, lclCargoType } = data
  const { commodities, commodity, commodityID, ...cargoLCLRest } = cargoLCL

  const commodityData = processCommodityData({
    commodities,
    commodity,
    commodityID,
    isMultipleCommoditiesUnNumbersEnabled,
  })

  return {
    ...cargoLCLRest,
    ...commodityData,
    items: lclCargoType === LclCargoType.ByDimensions ? cargoLCLByDimensionsItems : cargoLCLByWeightAndVolItems,
  }
}

type GetAddressesFromFormStateArgs = {
  formState: BookingWizardFormState
  fetchAddressAPI: (addressId: string) => Promise<PickupDeliveryLocationListResponseV1>
}

export async function getAddressesFromFormState({
  formState,
  fetchAddressAPI,
}: GetAddressesFromFormStateArgs): Promise<{
  pickupAddress?: string
  deliveryAddress?: string
}> {
  const { pickupAddressDetails, deliveryAddressDetails } = formState

  const legacyFormattedPickupAddress =
    pickupAddressDetails != null ? formatAddressDetails(pickupAddressDetails) : undefined
  const legacyFormattedDeliveryAddress =
    deliveryAddressDetails != null ? formatAddressDetails(deliveryAddressDetails) : undefined

  // Resolving the address ID against master data is a best-effort enrichment: if the lookup fails
  // (e.g. the service is unavailable), fall back to the locally formatted address rather than failing
  // the whole submission over something the user cannot act on.
  const fetchAddressOrUndefined = (addressID?: string) =>
    addressID
      ? fetchAddressAPI(addressID).catch((err) => {
          // Keep the failure observable in monitoring even though we degrade gracefully.
          logError('getAddressesFromFormState', 'Master-data address lookup failed, using local formatting', { err })
          return undefined
        })
      : Promise.resolve(undefined)

  const [MDpickupAddressAddress, MDdeliveryAddressAddress] = await Promise.all([
    fetchAddressOrUndefined(pickupAddressDetails?.addressID),
    fetchAddressOrUndefined(deliveryAddressDetails?.addressID),
  ]).then(([pickup, delivery]) => [pickup?.items[0], delivery?.items[0]])

  return {
    pickupAddress: MDpickupAddressAddress?.addressFormatted ?? legacyFormattedPickupAddress,
    deliveryAddress: MDdeliveryAddressAddress?.addressFormatted ?? legacyFormattedDeliveryAddress,
  }
}

type MapBookingWizardFormStateToRequestMQDTOArgs = {
  formState: BookingWizardFormState
  pricingResponse: PricingResponseShipmentWithManualQuote
  fetchAddressAPI: (addressId: string) => Promise<PickupDeliveryLocationListResponseV1>
}
export const mapBookingWizardFormStateToRequestMQDTO = async ({
  formState,
  pricingResponse,
  fetchAddressAPI,
}: MapBookingWizardFormStateToRequestMQDTOArgs): Promise<RequestManualQuoteDTO> => {
  const { pickupAddress, deliveryAddress } = await getAddressesFromFormState({ formState, fetchAddressAPI })
  const momainCarriageModes = formState.mainCarriageMode
  const modes =
    momainCarriageModes.length === 2 &&
    momainCarriageModes.includes(BookingDTOTransportMode.Air) &&
    momainCarriageModes.includes(BookingDTOTransportMode.Sea)
      ? ['seaAir']
      : momainCarriageModes
  return {
    quoteSheetId: pricingResponse.quoteSheetID,
    incoterm: mapIncotermToUniqueKey(formState.incoterm),
    modes,
    cargoReadyAt: new Date(formState.cargoReadyAt),
    pickupAddress,
    deliveryAddress,
    bookingParty: formState.controllingParty,
    customerNotes: formState.hasDangerousGoods ? formState.additionalCargoDetails : formState.customerNotes,
    additionalRequirements:
      formState?.additionalRequirements != null
        ? {
            ...formState.additionalRequirements,
            transportToPortMode: formState.additionalRequirements.transportToPortMode
              ? formState.additionalRequirements.transportToPortMode
              : undefined,
            EXWDate:
              formState.additionalRequirements?.EXWDate != null
                ? format(parseISO(formState.additionalRequirements?.EXWDate), 'yyyy-MM-dd')
                : undefined,
          }
        : undefined,
    buyersConsolidation: formState.isBCNBooking
      ? {
          enabled: formState.isBCNBooking,
          numberOfShipments: formState.bcnDetails?.numberOfShippers,
          numberOfShippersNotClear: formState.bcnDetails?.numberOfShippersNotClear,
          incoterms:
            formState.bcnDetails?.incoterm != null ? [mapIncotermToUniqueKey(formState.bcnDetails?.incoterm)] : [],
        }
      : undefined,
    consignee: formState.consignee,
    shipper: formState.shipper,
  } as RequestManualQuoteDTO
}

const getMainCarriageModeFromQuoteSheet = (quoteSheet: Sheet): WaypointBaseDTOTransportMode | undefined => {
  const { request } = quoteSheet
  if (request.modes?.length) {
    const mode = request.modes[0]
    if (mode === 'seaAir') {
      return 'air'
    }
    return mode
  }
  return undefined
}

type GetAddressFromMasterDataLocationsArguments = {
  location: Point['location']
  pickupDeliveryLocations: PickupDeliveryLocationResponseV1[]
}
export const getAddressFromMasterDataLocations = ({
  location,
  pickupDeliveryLocations,
}: GetAddressFromMasterDataLocationsArguments): AddressDetailsDTO | undefined => {
  const foundLocation = pickupDeliveryLocations.find(
    (l) =>
      'latitude' in location &&
      l.address.coordinates?.lat === location.latitude &&
      l.address.coordinates?.lng === location.longitude
  )

  if (foundLocation) {
    return createAddressDetailsFromLocation(foundLocation) ?? undefined
  }
  return undefined
}

export const getContainerTypeFromCargoTypes = (
  cargoTypes: CargoType[],
  containerType?: string
): ContainerItemDTO['type'] | undefined => {
  // First we try to match the container type with the code else with the isoTypeGroup
  // Why isoTypeGroup? https://github.com/freight-hub/booking-v2-core/blob/main/src/features/Quotes/GetQuoteSheet/BookingToQuoteRequestMapper.ts#L181
  const cargoType = cargoTypes.find(
    (ct) => ct.code === containerType || ct.containerAttributes.isoTypeGroup === containerType
  )
  return cargoType ? cargoType.code : undefined
}

type MapQuoteSheetToBookingWizardFormStateArguments = {
  quoteSheet?: Sheet
  pickupDeliveryLocations?: PickupDeliveryLocationResponseV1[]
  commodities: TranslatedCommodity[]
  isMultipleCommoditiesUnNumbersEnabled: boolean
  cargoTypes: CargoType[]
  isNewCustomsServiceEnabled: boolean
  isVgmLocalHandlingServicesEnabled?: boolean
}
export const mapQuoteSheetToBookingWizardFormState = ({
  quoteSheet,
  pickupDeliveryLocations = [],
  commodities = [],
  isMultipleCommoditiesUnNumbersEnabled,
  cargoTypes,
  isNewCustomsServiceEnabled,
  isVgmLocalHandlingServicesEnabled = false,
}: MapQuoteSheetToBookingWizardFormStateArguments): DefaultValues<BookingWizardFormState> => {
  if (!quoteSheet) {
    return bookingWizardDefaultBooking
  }
  const { request } = quoteSheet

  const quoteRequestCargoCommodity = request.cargo?.[0]?.commodityType ?? ''

  const commodityID = commodities.find((c) => Object.values(c.translations).includes(quoteRequestCargoCommodity))?.value

  // Multiple commodities / UN numbers are only enabled for Sea-FCL. Evaluate against the full
  // modes array so multimodal quotes (e.g. ['sea', 'rail']) and 'seaAir' are not treated as Sea-FCL.
  const isEffectiveMultiCommoditiesUnNumbersEnabled =
    isMultipleCommoditiesUnNumbersEnabled && isSeaFcl(request.type, request.modes)

  const commodityData = processCommodityData({
    commodities: commodityID ? [{ commodity: quoteRequestCargoCommodity, commodityID }] : [],
    commodity: commodityID ? quoteRequestCargoCommodity : undefined,
    commodityID,
    isMultipleCommoditiesUnNumbersEnabled: isEffectiveMultiCommoditiesUnNumbersEnabled,
  })

  const cargoFCL: DefaultValues<BookingDTOCargoFCL> | undefined =
    request.type === 'fcl'
      ? {
          ...defaultFCLCargo,
          containers: request.cargo.map((c) => ({
            type: getContainerTypeFromCargoTypes(cargoTypes, c.containerType) as ContainerItemDTO['type'],
            quantity: c.quantity,
          })),
          grossWeight: request.totalWeight,
          ...commodityData,
        }
      : undefined

  const cargoLCL: Pick<BookingWizardFormState, 'cargoLCL' | 'cargoLCLByDimensionsItems'> | undefined =
    request.type === 'lcl'
      ? {
          cargoLCL: {
            ...commodityData,
            isStackableGoods: request.services?.isStackable,
          },
          cargoLCLByDimensionsItems: request.cargo.map((c) => ({
            quantity: c.quantity,
            weight: c.grossWeight ?? 0,
            volume: c.volume ?? 0,
            // Convert cm to m for dimensions as MQ saves the dimensions in m
            // But BookingDTO expects dimensions in cm
            dimensions: {
              lengthCm: (c.length ?? 0) * 100,
              widthCm: (c.width ?? 0) * 100,
              heightCm: (c.height ?? 0) * 100,
            },
          })),
        }
      : undefined

  const customs: Pick<BookingWizardFormState, 'importCustomsService' | 'exportCustomsService' | 'customs'> =
    isNewCustomsServiceEnabled
      ? {
          importCustomsService: request.services?.customsIssuance
            ? {
                hsCodesCount: request.services?.hsCodesCount,
                clearanceTypes: request.services?.customsTypes[1]
                  ? [request.services?.customsTypes[1] as BookingDTOImportCustomsServiceClearanceTypes]
                  : undefined,
              }
            : undefined,
          exportCustomsService: request.services?.customsIssuance
            ? {
                hsCodesCount: request.services?.hsCodesCount,
                clearanceTypes: request.services?.customsTypes[0]
                  ? [request.services?.customsTypes[0] as BookingDTOImportCustomsServiceClearanceTypes]
                  : undefined,
              }
            : undefined,
        }
      : {
          customs: request.services?.customsIssuance
            ? {
                customsIssuance: request.services?.customsIssuance,
                originCustomsType: (request.services?.customsTypes[0] as BookingCustomType) ?? 'none',
                destinationCustomsType: (request.services?.customsTypes[1] as BookingCustomType) ?? 'none',
                hsCodesCount: request.services?.hsCodesCount,
              }
            : undefined,
        }

  const mainCarriageMode = getMainCarriageModeFromQuoteSheet(quoteSheet)

  return {
    mainCarriageMode: mainCarriageMode != null ? [mainCarriageMode] : [],
    transportType: request.type,
    hasDangerousGoods: request.services?.dangerousGoods,
    cargoReadyAt: request.cargoReadyAt ? request.cargoReadyAt.toString() : undefined,
    cargoFCL,
    ...cargoLCL,
    lclCargoType: LclCargoType.ByDimensions,
    controllingParty:
      request.bookingParty === 'seller' ? BookingDTOControllingParty.Shipper : BookingDTOControllingParty.Consignee,
    originPort: isPort(request.journey.origin) ? request.journey.origin._id : undefined,
    destinationPort: isPort(request.journey.destination) ? request.journey.destination._id : undefined,
    pickupAddressDetails: isAddress(request.journey.origin)
      ? getAddressFromMasterDataLocations({
          location: request.journey.origin.location,
          pickupDeliveryLocations,
        })
      : undefined,
    deliveryAddressDetails: isAddress(request.journey.destination)
      ? getAddressFromMasterDataLocations({
          location: request.journey.destination.location,
          pickupDeliveryLocations,
        })
      : undefined,
    // A quoted VGM prefills the VGMO service, or the legacy switch while the flag is off
    isVGMBooking: isVgmLocalHandlingServicesEnabled ? undefined : request.services?.vgm,
    extraLocalHandlingServicesEnabled: isVgmLocalHandlingServicesEnabled && request.services?.vgm === true,
    originLocalHandlingService:
      isVgmLocalHandlingServicesEnabled && request.services?.vgm === true ? [OriginLocalHandlingProductCode.VGMO] : [],
    isBCNBooking: request.services?.buyersConsolidation?.enabled,
    bcnDetails: request.services?.buyersConsolidation?.enabled
      ? {
          numberOfShippers: request.services.buyersConsolidation?.numberOfShipments,
          numberOfShippersNotClear: request.services.buyersConsolidation?.numberOfShipments == undefined,
          incoterm: {
            code: request.services?.buyersConsolidation?.incoterms?.[0],
            namedPlaceOfDelivery:
              incotermCodeToNamedPlaceOfDelivery[request.services?.buyersConsolidation?.incoterms?.[0]],
          },
        }
      : undefined,
    specialEquipment: request.services?.specialEquipment ?? false,
    carbonOffsetContribution: request.services?.carbonOffset?.offsetStrategy === 'all',
    incoterm: {
      code: request.incoTerm,
      namedPlaceOfDelivery: request.incoTerm ? incotermCodeToNamedPlaceOfDelivery[request.incoTerm] : undefined,
    },
    ...customs,
    insurance: request.services?.insurances as InsuranceDTO[],
  }
}

type BookingFCLCommodityDTO = Pick<BookingDTOCargoFCL, 'commodities' | 'commodity' | 'commodityID'>
type ProccessCommodityDataArgs = BookingFCLCommodityDTO & {
  isMultipleCommoditiesUnNumbersEnabled: boolean
}
export const processCommodityData = ({
  commodities,
  commodity,
  commodityID,
  isMultipleCommoditiesUnNumbersEnabled,
}: ProccessCommodityDataArgs): BookingFCLCommodityDTO => {
  if (isMultipleCommoditiesUnNumbersEnabled) {
    if (commodity && commodityID && (isNil(commodities) || commodities.length === 0)) {
      return { commodities: [{ commodity, commodityID }] }
    } else if (isNil(commodities) || commodities.length === 0) {
      return { commodities: [] }
    } else {
      return {
        commodities,
      }
    }
  } else {
    return {
      commodity,
      commodityID,
    }
  }
}
